import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import ComicCard from "@/components/base/ComicCard";
import { comics } from "@/mocks/comics";

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const [collected, setCollected] = useState(false);
  const [lastRead, setLastRead] = useState(() => {
    try {
      return window.localStorage.getItem(`cechi-read-${id}`) || "1";
    } catch {
      return "1";
    }
  });

  const related = useMemo(
    () => comics.filter((c) => c.category === comic?.category && c.id !== id).slice(0, 5),
    [comic, id],
  );

  if (!comic) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
          <i className="ri-ghost-line text-5xl text-foreground-300"></i>
          <p className="mt-4 text-sm text-foreground-600">未找到该漫画</p>
          <Link to="/category" className="mt-4 rounded-full bg-primary-600 px-6 py-2.5 text-sm text-background-50 hover:bg-primary-700">
            返回分类
          </Link>
        </div>
      </Layout>
    );
  }

  const lastIndex = Number(lastRead) - 1;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        {/* Breadcrumb */}
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
          <a href="/" className="hover:text-primary-600">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href="/category" className="hover:text-primary-600">分类</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href={`/category?type=${encodeURIComponent(comic.category)}`} className="hover:text-primary-600">
            {comic.category}
          </a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">{comic.title}</span>
        </nav>

        {/* Header card */}
        <section className="mt-5 overflow-hidden rounded-2xl border border-background-200/80 bg-background-50">
          <div className="flex flex-col gap-6 p-5 md:flex-row md:p-7">
            <div className="shrink-0 overflow-hidden rounded-xl border border-background-200/70">
              <img
                src={comic.cover}
                alt={`${comic.title}漫画封面`}
                className="aspect-[3/4] w-full object-cover object-top sm:w-56"
              />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium text-background-50 ${
                    comic.status === "连载中" ? "bg-accent-500" : "bg-primary-600"
                  }`}
                >
                  {comic.status}
                </span>
                <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">{comic.category}</span>
              </div>
              <h1 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">{comic.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong className="text-foreground-950">{comic.rating.toFixed(1)}</strong>
                </span>
                <span>人气 {comic.views}</span>
                <span>{comic.follows.toLocaleString()}人收藏</span>
                <span>作者:{comic.author}</span>
              </div>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700">{comic.intro}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 dark:bg-secondary-100/70">
                    #{t}
                  </span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/read/${comic.id}?chapter=${lastIndex + 1}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-fill"></i>
                  {lastIndex >= 0 && lastIndex < comic.chapters.length ? "继续阅读" : "开始阅读"}
                </Link>
                <button
                  type="button"
                  onClick={() => setCollected((v) => !v)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-6 py-3 text-sm font-bold transition-colors ${
                    collected
                      ? "border-accent-500 bg-accent-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-800 hover:border-accent-500 hover:text-accent-500"
                  }`}
                >
                  <i className={collected ? "ri-star-fill" : "ri-star-line"}></i>
                  {collected ? "已收藏" : "收藏"}
                </button>
                <span className="text-xs text-foreground-500">最近更新:{comic.updated} · 共{comic.chapters.length}话</span>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter directory */}
        <section className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950 md:text-2xl">
              <i className="ri-list-unordered text-accent-500"></i>
              章节目录
            </h2>
            <span className="text-sm text-foreground-500">共 {comic.chapters.length} 话</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 md:gap-3 lg:grid-cols-4">
            {comic.chapters.map((ch, i) => (
              <Link
                key={ch}
                to={`/read/${comic.id}?chapter=${i + 1}`}
                className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                  i === lastIndex
                    ? "border-accent-500 bg-accent-500 text-background-50"
                    : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
                }`}
              >
                <span className="truncate">{ch}</span>
                {i === lastIndex && <i className="ri-bookmark-fill shrink-0"></i>}
              </Link>
            ))}
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">相关推荐</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Article structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: comic.title,
            author: comic.author,
            description: comic.intro,
            genre: comic.category,
            keywords: comic.tags.join(","),
            image: comic.cover,
            url: `https://faequest.github.io/comic/${comic.id}`,
          }),
        }}
      />
    </Layout>
  );
}